import {Selector, State} from '@ngxs/store';
import {ModalHotPlaylistsModel} from '@store/modals-state/hot-playlists/modal-hot-playlists.model';
import {Injectable} from '@angular/core';
import {IHotPlaylistDevice} from '@interfaces/devices';
import {IHotPlaylistGroup} from '@interfaces/group';
import {MatLegacyCheckboxChange as MatCheckboxChange} from '@angular/material/legacy-checkbox';
import {NgxsDataRepository} from '@angular-ru/ngxs/repositories';
import {Payload, StateRepository} from '@angular-ru/ngxs/decorators';
import {DataActionWithoutSubscription} from '@shared/functions';


@StateRepository()
@State<ModalHotPlaylistsModel>({
    name: 'ModalHotPlaylist',
    defaults: {
        devicesOnPage: [],
        groupsOnPage: [],
        totalDevices: 0,
        totalGroups: 0,
        currentDevicesPage: 1,
        currentGroupPage: 1,
        selectedDeviceIdsFromDb: [],
        deviceIdsWithCheckbox: [],
        deviceIdsToAdd: [],
        deviceIdsToRemove: [],
        selectedGroupsIdsFromDb: [],
        groupsIdsToAdd: [],
        groupsIdsToRemove: [],
        groupIdsWithCheckbox: [],
        itemsPerPage: 50
    }
})

@Injectable()
export class ModalHotPlaylistsState extends NgxsDataRepository<ModalHotPlaylistsModel> {
    constructor() {
        super();
    }

    @Selector([ModalHotPlaylistsState])
    static getDevicesOnPage(state: ModalHotPlaylistsModel): IHotPlaylistDevice[] {
        return state.devicesOnPage;
    }

    @Selector([ModalHotPlaylistsState])
    static getGroupsOnPage(state: ModalHotPlaylistsModel): IHotPlaylistGroup[] {
        return state.groupsOnPage;
    }

    @Selector([ModalHotPlaylistsState])
    static getDevicesIdsWithCheckBox(state: ModalHotPlaylistsModel): number[] {
        return state.deviceIdsWithCheckbox;
    }

    @Selector([ModalHotPlaylistsState])
    static getGroupsIdsWithCheckBox(state: ModalHotPlaylistsModel): number[] {
        return state.groupIdsWithCheckbox;
    }

    @DataActionWithoutSubscription()
    setDevices(@Payload('[Modal hot playlist] set selected devices from DB') devicesOnPage: IHotPlaylistDevice[],
               selectedDevicesFromDb: IHotPlaylistDevice[], total: number): void {
        const state = this.ctx.getState();
        const devicesToRemove = state.deviceIdsToRemove;
        const selectedDevices = new Set([
            ...selectedDevicesFromDb.filter((id) => !devicesToRemove.includes(id.id)).map(d => d.id),
            ...state.deviceIdsWithCheckbox
        ]).values();
        const selectedFromDb = new Set([...selectedDevicesFromDb.map(d => d.id), ...state.selectedDeviceIdsFromDb]).values();

        this.ctx.patchState({
            selectedDeviceIdsFromDb: Array.from(selectedFromDb),
            deviceIdsWithCheckbox: Array.from(selectedDevices),
            devicesOnPage, totalDevices: total
        });
    }

    @DataActionWithoutSubscription()
    onDeviceCheckboxEvent(
        @Payload('[Modal hot playlist] device checkbox event')
            $event: MatCheckboxChange, device: IHotPlaylistDevice): void {
        const state = this.ctx.getState();
        const activated = state.deviceIdsWithCheckbox;

        if ($event.checked) {
            const devicesToAdd = !device.in_hot_playlist ? [...state.deviceIdsToAdd, device.id] : [...state.deviceIdsToAdd];
            const devicesToRemove = state.deviceIdsToRemove.filter((d) => d !== device.id);
            this.ctx.patchState({
                deviceIdsWithCheckbox: [...activated, device.id],
                deviceIdsToAdd: devicesToAdd, deviceIdsToRemove: devicesToRemove
            });
        } else {
            const filteredIds = activated.filter((id) => id !== device.id);
            const devicesToRemove = device.in_hot_playlist ? [...state.deviceIdsToRemove, device.id] : [...state.deviceIdsToRemove];
            const devicesToAdd = state.deviceIdsToAdd.filter((id) => id !== device.id);
            this.ctx.patchState({
                deviceIdsWithCheckbox: filteredIds,
                deviceIdsToRemove: devicesToRemove,
                deviceIdsToAdd: devicesToAdd
            });
        }
    }

    @DataActionWithoutSubscription()
    setGroups(@Payload('[Modal hot playlist] set selected groups from DB') groupsOnPage: IHotPlaylistGroup[],
              groups: IHotPlaylistGroup[], total: number): void {
        const state = this.ctx.getState();
        const groupsToRemove = state.groupsIdsToRemove;
        const selectedGroups = new Set([
            ...groups.filter((id) => !groupsToRemove.includes(id.group_id)).map(g => g.group_id),
            ...state.groupIdsWithCheckbox
        ]).values();
        const selectedFromDb = new Set([...groups.map(g => g.group_id), ...state.selectedGroupsIdsFromDb]).values();

        this.ctx.patchState({
            selectedGroupsIdsFromDb: Array.from(selectedFromDb),
            groupIdsWithCheckbox: Array.from(selectedGroups),
            groupsOnPage, totalGroups: total
        });
    }

    @DataActionWithoutSubscription()
    onGroupCheckboxEvent(
        @Payload('[Modal hot playlist] group checkbox event')
            $event: MatCheckboxChange, group: IHotPlaylistGroup
    ): void {
        const state = this.ctx.getState();
        const activated = state.groupIdsWithCheckbox;
        if ($event.checked) {
            const groupsToAdd = !group.in_hot_playlist ? [...state.groupsIdsToAdd, group.group_id] : [...state.groupsIdsToAdd];
            const groupsToRemove = state.groupsIdsToRemove.filter(id => id !== group.group_id);
            this.ctx.patchState({
                groupIdsWithCheckbox: [...activated, group.group_id],
                groupsIdsToAdd: groupsToAdd,
                groupsIdsToRemove: groupsToRemove
            });
        } else {
            const groupsToRemove = group.in_hot_playlist ? [...state.groupsIdsToRemove, group.group_id] : [...state.groupsIdsToRemove];
            const groupsToAdd = state.groupsIdsToAdd.filter(id => id !== group.group_id);
            const filteredIds = activated.filter((id) => id !== group.group_id);

            this.ctx.patchState({
                groupIdsWithCheckbox: filteredIds,
                groupsIdsToAdd: groupsToAdd,
                groupsIdsToRemove: groupsToRemove
            });
        }
    }

    @DataActionWithoutSubscription()
    setDevicesPage(@Payload('[Modal hot playlist] set devices page') pageNum: number): void {
        this.ctx.patchState({currentDevicesPage: pageNum});
    }

    @DataActionWithoutSubscription()
    setGroupsPage(@Payload('[Modal hot playlist] set groups page') pageNum: number): void {
        this.ctx.patchState({currentGroupPage: pageNum});
    }

    @DataActionWithoutSubscription()
    clearSelected(): void {
        const state = this.ctx.getState();
        const devicesOnPage = state.devicesOnPage;
        const groupsOnPage = state.groupsOnPage;
        const groupIdsWithCheckbox = state.selectedGroupsIdsFromDb;
        const deviceIdsWithCheckbox = state.selectedDeviceIdsFromDb;
        this.ctx.setState(
            {
                ...state,
                devicesOnPage,
                groupsOnPage,
                groupIdsWithCheckbox,
                deviceIdsWithCheckbox,
                deviceIdsToAdd: [],
                deviceIdsToRemove: [],
                groupsIdsToAdd: [],
                groupsIdsToRemove: [],
            }
        );
    }
}
